import {defineStore} from 'pinia'
import usePim from '~/composables/pim_json_loader'

export interface MenuCategory {
  technicalName: string
  url: string
  subcategories?: MenuCategory[]
  [key: string]: any
}

function normalizeMenuUrl(url: string = ''): string {
  return url.endsWith('/') ? url.slice(0, -1) : url
}

function findMenuItemByUrl(url: string, items: MenuCategory[], visited: Set<MenuCategory> = new Set()): MenuCategory | null {
  if (!url || !Array.isArray(items)) {
    return null
  }
  const target = normalizeMenuUrl(url)
  for (const item of items) {
    if (!item || visited.has(item)) {
      continue
    }
    visited.add(item)
    if (normalizeMenuUrl(item.url) === target) {
      return item
    }
    if (item.subcategories?.length) {
      const found = findMenuItemByUrl(url, item.subcategories, visited)
      if (found) {
        return found
      }
    }
  }
  return null
}

function findMenuItemByTechnicalName(technicalName: string, items: MenuCategory[], visited: Set<MenuCategory> = new Set()): MenuCategory | null {
  if (!technicalName || !Array.isArray(items)) {
    return null
  }
  const target = technicalName.toLowerCase()
  for (const item of items) {
    if (!item || visited.has(item)) {
      continue
    }
    visited.add(item)
    if (item.technicalName?.toLowerCase() === target) {
      return item
    }
    if (item.subcategories?.length) {
      const found = findMenuItemByTechnicalName(technicalName, item.subcategories, visited)
      if (found) {
        return found
      }
    }
  }
  return null
}

export const useCategoriesMenuStore = defineStore('categoriesMenu', {
  state: () => ({
    menu: [] as MenuCategory[],
    region: '',
    locale: '',
    loadingKey: null as string | null,
    loadingPromise: null as Promise<MenuCategory[]> | null
  }),
  actions: {
    async loadMenu(region: string, locale: string) {
      const normalizedRegion = (region || '').toLowerCase()
      const requestKey = `${normalizedRegion}|${locale}`

      if (this.menu.length && this.region === normalizedRegion && this.locale === locale) {
        return this.menu
      }

      // reuse the in-flight promise only if it's loading the same region+locale
      if (this.loadingPromise && this.loadingKey === requestKey) {
        return this.loadingPromise
      }

      this.loadingKey = requestKey
      this.loadingPromise = usePim<MenuCategory[]>(`v1/${normalizedRegion}/${locale}/categories_menu.json`)
        .then((json) => {
          this.menu = json || []
          this.region = normalizedRegion
          this.locale = locale
          return this.menu
        })
        .finally(() => {
          this.loadingPromise = null
          this.loadingKey = null
        })

      return this.loadingPromise
    },
    findByUrl(url: string, items?: MenuCategory[]): MenuCategory | null {
      return findMenuItemByUrl(url, items ?? this.menu)
    },
    findByTechnicalName(technicalName: string, items?: MenuCategory[]): MenuCategory | null {
      return findMenuItemByTechnicalName(technicalName, items ?? this.menu)
    }
  }
})
